/**
 * XHR transport — Kaynak HttpClient.ts (performXHRRequest/parseXHRResponse) port'u.
 * Karşılık: kaynak_app/src/lib/HttpClient.ts (784-855, 319-366).
 *
 * Neden XHR (Kaynak/Discord paterni): tek birleşik HTTP client. XHR'ın fetch'e
 * karşı avantajı = upload PROGRESS (xhr.upload.onprogress — fetch yapamaz), native
 * timeout, abort. apiFetch bunu transport olarak kullanır → tüm istekler Network'te
 * "xhr" görünür (Discord/Kaynak ile aynı), token-refresh + camel/snake KORUNUR.
 *
 * `fetch()` Response'unun apiFetch'in kullandığı alt-kümesini taklit eder
 * (ok/status/statusText/headers/text()/json()) → çağıran kod değişmez.
 */

export interface XhrResponseLike {
  ok: boolean;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  text: () => Promise<string>;
  json: () => Promise<unknown>;
}

export interface XhrInit {
  method?: string;
  headers?: Record<string, string>;
  body?: XMLHttpRequestBodyInit | null;
  signal?: AbortSignal | null;
  /** ms; 0/undefined → sınırsız. */
  timeout?: number;
  /** Upload progress (dosya yükleme çubuğu) — fetch'in YAPAMADIĞI şey. */
  onUploadProgress?: (event: ProgressEvent) => void;
  /** İkili indirme (blob). */
  binary?: boolean;
  /** Cross-origin cookie (Bearer-auth'ta gerekmez; default false). */
  withCredentials?: boolean;
}

function parseHeaders(raw: string | null): Record<string, string> {
  const map: Record<string, string> = {};
  if (!raw) return map;
  for (const line of raw.trim().split(/[\r\n]+/)) {
    const parts = line.split(": ");
    const name = parts.shift();
    const value = parts.join(": ");
    if (name) map[name.toLowerCase()] = value;
  }
  return map;
}

/** fetch(url, init) imzasını taklit eden XHR isteği. */
export function xhrRequest(url: string, init: XhrInit = {}): Promise<XhrResponseLike> {
  return new Promise<XhrResponseLike>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const method = (init.method ?? "GET").toUpperCase();

    if (init.onUploadProgress) {
      xhr.upload.addEventListener("progress", (e) => init.onUploadProgress?.(e));
    }

    xhr.addEventListener("load", () => {
      const status = xhr.status;
      const binary = !!init.binary;
      const responseText = binary ? "" : xhr.responseText;
      const blob = binary ? (xhr.response as Blob) : null;
      resolve({
        ok: status >= 200 && status < 300,
        status,
        statusText: xhr.statusText,
        headers: parseHeaders(xhr.getAllResponseHeaders()),
        text: async () => responseText,
        json: async () => (responseText ? JSON.parse(responseText) : undefined),
        // binary tüketicisi için blob'a erişim (gerekirse genişletilir)
        ...(binary ? { blob: async () => blob } : {}),
      } as XhrResponseLike);
    });
    xhr.addEventListener("error", () => reject(new Error("Network error during request")));
    xhr.addEventListener("abort", () => reject(new DOMException("Request aborted", "AbortError")));
    xhr.addEventListener("timeout", () => reject(new DOMException("Request timeout", "TimeoutError")));

    if (init.signal) {
      if (init.signal.aborted) {
        xhr.abort();
        return;
      }
      const onAbort = () => xhr.abort();
      init.signal.addEventListener("abort", onAbort);
      xhr.addEventListener("loadend", () => init.signal?.removeEventListener("abort", onAbort));
    }

    xhr.open(method, url);
    if (init.binary) xhr.responseType = "blob";
    if (init.timeout && init.timeout > 0) xhr.timeout = init.timeout;
    if (init.withCredentials) xhr.withCredentials = true;
    for (const [name, value] of Object.entries(init.headers ?? {})) {
      xhr.setRequestHeader(name, value);
    }

    xhr.send((init.body ?? null) as XMLHttpRequestBodyInit);
  });
}
